import clsx from 'clsx';
import { forwardRef, useRef, useState } from 'react';

import { textSm } from '../../typography';
import { SummaryDetails } from '../SummaryDetails/SummaryDetails';

import * as styles from './Accordion.module.css';

import type { TrackingProps } from '../types';

interface AccordionHeaderProps {
  before?: React.ReactNode;
  after?: React.ReactNode;
  className?: string;
  transparent?: boolean;
}

interface AccordionProps extends AccordionHeaderProps, TrackingProps {
  title: React.ReactNode;
  className?: string;
  Wrapper?: React.ComponentType<WrapperProps>;
  toggleRef?: React.MutableRefObject<HTMLElement | null>;
  children?: React.ReactNode;
}

const _AccordionHeader = forwardRef<HTMLDivElement, Omit<React.PropsWithChildren<AccordionHeaderProps>, 'transparent'>>(
  ({ before, after, className, children }, ref) => (
    <div className={clsx(styles.header, className)}>
      {before && <div className={styles.icon}>{before}</div>}
      <div ref={ref} className={styles.title}>
        {children}
      </div>
      {after && <div className={styles.toggle}>{after}</div>}
    </div>
  ),
);

const AccordionHeader: React.FC<AccordionHeaderProps> = ({ className, transparent = false, ...props }) => (
  <_AccordionHeader {...props} className={clsx(styles.wrapper, transparent && styles.transparent, className)} />
);

type WrapperProps = {
  className: string;
  isVisible: boolean;
  children: React.ReactNode;
};

const DefaultWrapper: React.FC<WrapperProps> = ({ className, children }) => <div className={className}>{children}</div>;

const AccordionDetails: React.FC<{ className?: string; children: React.ReactNode }> = ({ className, children }) => (
  <div className={clsx(styles.content, className, textSm)}>{children}</div>
);

const Accordion: React.FC<AccordionProps> = ({
  before,
  after,
  title,
  children,
  className,
  transparent = false,
  toggleRef,
  Wrapper = DefaultWrapper,
  trackingId,
}) => {
  const backupToggleRef = useRef<HTMLAnchorElement | HTMLButtonElement | null>(null);
  const [isOpened, setIsOpened] = useState(false);

  const handleToggle = (open: boolean): void => {
    setIsOpened(open);
  };

  return (
    <SummaryDetails
      className={clsx(styles.wrapper, transparent && styles.transparent, className)}
      summary={
        <_AccordionHeader
          before={before}
          after={after}
          ref={(el) => {
            backupToggleRef.current = el?.getElementsByTagName('a')[0] ?? el?.getElementsByTagName('button')[0] ?? null;
          }}
        >
          {title}
        </_AccordionHeader>
      }
      details={
        <Wrapper
          className={[styles.contentWrapper, isOpened ? styles.expanded : undefined].join(' ')}
          isVisible={isOpened}
        >
          <AccordionDetails>{children}</AccordionDetails>
        </Wrapper>
      }
      detailsRef={toggleRef ?? backupToggleRef}
      onDetailsToggle={handleToggle}
      trackingId={trackingId}
    />
  );
};

export { Accordion, AccordionDetails, AccordionHeader };
